import { useCallback, useEffect, useRef, useState } from "react";
import { useApiOperation } from "src/api/hooks";
import { getPartnerInformationAsync } from "src/api/partner/partner-api";
import { PartnerFeature } from "src/api/partner/partner-types";
import { getSelfVisitGroupListAsync } from "src/api/visitor/visitor-api";
import { ApprovalStatus, CtSelfVisitGroupListItem, VisitStatus } from "src/api/visitor/visitor-types";
import { MemberContractModel } from "src/api/contract/contract-types";

/**
 * 방문신청 승인 - 처리 대기(APPROVAL_PENDING) 건수 조회 훅 (My 화면용 — 전체 집계)
 *
 * - 사용자가 보유한 모든 계약 중 방문신청 승인 feature(visitorApprovalFeatures) 를 가진 파트너의
 *   APPROVAL_PENDING 건수를 (partnerId, contractApplyNumber) 쌍 단위로 조회 후 합산한다.
 * - 동일 쌍이 중복될 수 있어 Set 으로 dedup.
 *
 * 반환값:
 *  - hasFeature: 본 기능 노출 여부 판단용
 *  - pendingCount: 합산된 승인 대기 건수
 *  - isError: 어느 한 호출이라도 실패 시 true
 */

const visitorApprovalVisibleContractSteps = ["USE_APPROVAL", "USE_PROGRESS", "TERMINATE_RECEIVED"];

/** 방문신청 승인 메뉴를 노출시키는 feature 합집합 (하나라도 보유 시 노출) */
export const visitorApprovalFeatures: PartnerFeature[] = [
  PartnerFeature.FEATURE_VISITOR_SELF_REGIST,
  PartnerFeature.FEATURE_VISITOR_TENANT_MANAGER_APPROVAL,
];

/** 파트너가 방문신청 승인 feature 를 하나라도 보유하는지 여부 */
export const hasVisitorApprovalFeature = (features?: PartnerFeature[]): boolean =>
  !!features?.some((feature) => visitorApprovalFeatures.includes(feature));

interface UseVisitorApprovalPendingCountResult {
  hasFeature: boolean;
  pendingCount: number;
  isError: boolean;
}

export const useVisitorApprovalPendingCount = (contracts: MemberContractModel[]): UseVisitorApprovalPendingCountResult => {
  const { executeAsync: getPartnerInformation } = useApiOperation(getPartnerInformationAsync, { noHandleError: true });
  const { executeAsync: getSelfVisitGroupList } = useApiOperation(getSelfVisitGroupListAsync, { noHandleError: true });

  const [hasFeature, setHasFeature] = useState<boolean>(false);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [isError, setIsError] = useState<boolean>(false);

  // 호출 중복 방지용 마지막 실행 키
  const lastKeyRef = useRef<string>("");

  const stableKey = (contracts || [])
    .filter((c) => !!c.contractStep && visitorApprovalVisibleContractSteps.includes(c.contractStep))
    .map((c) => `${c.contractId ?? ""}::${c.contractApplyNumber ?? ""}`)
    .sort()
    .join("|");

  const fetchAll = useCallback(async (key: string) => {
    const visibleContracts = (contracts || []).filter(
      (c) => !!c.contractStep && visitorApprovalVisibleContractSteps.includes(c.contractStep),
    );

    if (visibleContracts.length === 0) {
      setHasFeature(false);
      setPendingCount(0);
      setIsError(false);
      return;
    }

    // 1) 각 계약의 파트너 정보 병렬 조회
    const partnerResults = await Promise.allSettled(
      visibleContracts.map((c) =>
        c.contractId !== undefined && c.contractId !== null ? getPartnerInformation({ contractId: c.contractId }) : Promise.reject(new Error("no contractId")),
      ),
    );

    if (lastKeyRef.current !== key) return; // 실행 도중 deps 변경 시 무시

    let partnerErrored = false;
    const pairs = new Set<string>();
    partnerResults.forEach((result, idx) => {
      if (result.status !== "fulfilled") {
        partnerErrored = true;
        return;
      }
      const res = result.value;
      // useApiOperation 은 에러 시 noHandleError 옵션 + try/catch 부재로 정상 응답 객체 모양이 아닐 수 있음
      const partner = res?.data?.data?.partner;
      const features = partner?.features;
      const partnerId = partner?.id;
      const contractApplyNumber = visibleContracts[idx]?.contractApplyNumber;
      if (
        partnerId !== undefined &&
        partnerId !== null &&
        !!contractApplyNumber &&
        hasVisitorApprovalFeature(features)
      ) {
        pairs.add(`${partnerId}::${contractApplyNumber}`);
      }
    });

    const featureOn = pairs.size > 0;
    setHasFeature(featureOn);

    if (!featureOn) {
      setPendingCount(0);
      setIsError(partnerErrored);
      return;
    }

    // 2) 각 쌍별 처리 대기 그룹 목록 조회 후 클라이언트 필터링
    // 만료(visitEndTime 경과) · 취소(VISIT_DELETE) 건은 사용자가 더 이상 처리할 수 없으므로 카운트에서 제외한다.
    const pairList = Array.from(pairs);
    const groupResults = await Promise.allSettled(
      pairList.map((pair) => {
        const [partnerId, contractApplyNumber] = pair.split("::");
        return getSelfVisitGroupList({
          partnerId,
          query: {
            contractApplyNumber,
            memberApprovalStatus: ApprovalStatus.APPROVAL_PENDING,
            page: 0,
            size: 1000,
          },
        });
      }),
    );

    if (lastKeyRef.current !== key) return;

    let countErrored = partnerErrored;
    const mergedGroups: CtSelfVisitGroupListItem[] = [];
    groupResults.forEach((result) => {
      if (result.status !== "fulfilled") {
        countErrored = true;
        return;
      }
      const content = result.value?.data?.data?.content ?? [];
      mergedGroups.push(...content);
    });

    // visitApplyNumber 기준 dedupe (동일 그룹이 여러 쿼리 결과에 걸쳐 등장하는 경우 방어)
    const dedupedGroups = Array.from(new Map(mergedGroups.map((g) => [g.visitApplyNumber, g])).values());

    const now = Date.now();
    const total = dedupedGroups.filter(
      (g) =>
        g.memberApprovalStatus === ApprovalStatus.APPROVAL_PENDING &&
        !!g.visitEndTime &&
        new Date(g.visitEndTime).getTime() >= now &&
        g.visitStatus !== VisitStatus.VISIT_DELETE,
    ).length;

    setPendingCount(total);
    setIsError(countErrored);
  }, [contracts, getPartnerInformation, getSelfVisitGroupList]);

  useEffect(() => {
    if (!stableKey) {
      lastKeyRef.current = "";
      setHasFeature(false);
      setPendingCount(0);
      setIsError(false);
      return;
    }
    if (lastKeyRef.current === stableKey) return;
    lastKeyRef.current = stableKey;
    fetchAll(stableKey);
  }, [stableKey, fetchAll]);

  return { hasFeature, pendingCount, isError };
};
